import { useEffect, useRef, useCallback } from 'react';
import { anglesService } from '@/shared/api/services/angles.service';
import {
  AngleValidationStatus,
  isAngleValidationFinished,
} from '@/shared/constants/status';
import {
  VALIDATION_POLL_INTERVAL_MS as POLL_INTERVAL_MS,
  VALIDATION_POLL_INITIAL_DELAY_MS as INITIAL_DELAY_MS,
  VALIDATION_PROCESSING_TIMEOUT_MS as PROCESSING_TIMEOUT_MS,
} from '@/features/angle/lib/validation-polling';
import type { ValidationItem, UploadPhase } from './types';
import { decodeValidation } from './decode-validation';

interface UseValidationPollingProps {
  phase: UploadPhase;
  validationItems: ValidationItem[];
  setValidationItems: React.Dispatch<React.SetStateAction<ValidationItem[]>>;
  notSuitableImageMessage: string;
  validationTimeoutMessage: string;
}

export function useValidationPolling({
  phase,
  validationItems,
  setValidationItems,
  notSuitableImageMessage,
  validationTimeoutMessage,
}: UseValidationPollingProps) {
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const pollingDelayRef = useRef<NodeJS.Timeout | null>(null);
  // Track processing elapsed time per angle (not total attempts)
  const processingElapsedRef = useRef<Map<string, number>>(new Map());

  // Use ref for validationItems to avoid stale closure in setInterval
  const validationItemsRef = useRef(validationItems);
  validationItemsRef.current = validationItems;

  useEffect(() => {
    if (phase !== 'validating' || validationItems.length === 0) {
      if (pollingDelayRef.current) {
        clearTimeout(pollingDelayRef.current);
        pollingDelayRef.current = null;
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
        pollingIntervalRef.current = null;
      }
      return;
    }

    const hasValidatingItems = validationItems.some(
      (item) => item.status === 'validating'
    );

    if (hasValidatingItems && !pollingIntervalRef.current) {
      processingElapsedRef.current = new Map();

      // Wait before starting polling — unified across all platforms
      pollingDelayRef.current = setTimeout(() => {
        pollingIntervalRef.current = setInterval(async () => {
          // Read from ref to avoid stale closure
          const itemsToCheck = validationItemsRef.current.filter(
            (item) => item.status === 'validating'
          );

          if (itemsToCheck.length === 0) {
            if (pollingIntervalRef.current) {
              clearInterval(pollingIntervalRef.current);
              pollingIntervalRef.current = null;
            }
            return;
          }

          for (const item of itemsToCheck) {
            try {
              const angleData = await anglesService.getAngle(item.id);
              const validation = angleData.attributes?.validation;

              if (!validation) {
                // Still pending (in queue) — don't count towards timeout
                continue;
              }

              if (validation.status === AngleValidationStatus.PENDING) {
                // In queue — don't count towards timeout
                continue;
              }

              if (validation.status === AngleValidationStatus.PROCESSING) {
                // Processing started — count towards timeout
                const elapsed =
                  (processingElapsedRef.current.get(item.id) ?? 0) +
                  POLL_INTERVAL_MS;
                processingElapsedRef.current.set(item.id, elapsed);

                if (elapsed >= PROCESSING_TIMEOUT_MS) {
                  // Timeout only when processing takes too long
                  setValidationItems((prev) =>
                    prev.map((vi) =>
                      vi.id === item.id
                        ? {
                            ...vi,
                            status: 'error' as const,
                            error: validationTimeoutMessage,
                          }
                        : vi
                    )
                  );
                }
                continue;
              }

              if (isAngleValidationFinished(validation.status)) {
                // One decoder for all three validation sources — handles the
                // lenient-vs-strict different-building split and every routed
                // outcome. See decode-validation.ts.
                const decoded = decodeValidation(
                  {
                    status: validation.status,
                    recommendations: validation.recommendations,
                    routed_to_documents: validation.routed_to_documents,
                    routed_to_references: validation.routed_to_references,
                    routed_to_output_style: validation.routed_to_output_style,
                    validation_failure_reason:
                      validation.validation_failure_reason,
                    can_override_building: validation.can_override_building,
                    auto_accepted_as_view: validation.auto_accepted_as_view,
                  },
                  notSuitableImageMessage
                );

                setValidationItems((prev) =>
                  prev.map((vi) =>
                    vi.id === item.id
                      ? {
                          ...vi,
                          status: decoded.status,
                          note: decoded.note,
                          error: decoded.error,
                          validation: decoded.validation,
                        }
                      : vi
                  )
                );
              }
            } catch (error) {
              console.error(
                `[useValidationPolling] Polling error for angle ${item.id}:`,
                error
              );
            }
          }
        }, POLL_INTERVAL_MS);
      }, INITIAL_DELAY_MS);
    }

    return () => {
      if (pollingDelayRef.current) {
        clearTimeout(pollingDelayRef.current);
      }
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [
    phase,
    validationItems,
    setValidationItems,
    notSuitableImageMessage,
    validationTimeoutMessage,
  ]);

  const clearPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }
    if (pollingDelayRef.current) {
      clearTimeout(pollingDelayRef.current);
      pollingDelayRef.current = null;
    }
  }, []);

  return { clearPolling };
}
